// src/components/register/RegistrationPage.tsx
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { httpsCallable } from 'firebase/functions';
import { functions, db, auth } from '../../config/firebase';
import { signInAnonymously } from 'firebase/auth';
import { registrationFormSchema } from '../../schemas/registration';
import { DEFAULT_VALUES, type RegistrationFormData } from '../../types/registration';
import { VideoSelector } from './VideoSelector';
import { ImageSelector } from './ImageSelector';
import { ImageUploader } from './ImageUploader';
import { collection, addDoc, onSnapshot } from 'firebase/firestore';

// shadcn/ui components
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

/**
 * 代理店セルフ登録ページ
 */
// 登録完了レスポンスの型定義
interface RegistrationResponse {
  success: boolean;
  agencyId: string;
  shortTokenId: string;
  urls: {
    lp: string;
    simulator: string;
  };
  error?: string;
}

// コンポーネントProps
interface RegistrationPageProps {
  isDemo?: boolean; // デモモード（決済スキップ）
}

export const RegistrationPage: React.FC<RegistrationPageProps> = ({ isDemo = false }) => {
  // デモモードではStep 5（決済）をスキップ
  const TOTAL_STEPS = isDemo ? 4 : 5;
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [isCompleted, setIsCompleted] = useState<boolean>(false);
  const [registrationResult, setRegistrationResult] = useState<RegistrationResponse | null>(null);
  const [_errorMessage, setErrorMessage] = useState<string>('');
  const [processingPayment, setProcessingPayment] = useState<boolean>(false);

  // React Hook Form
  const form = useForm<RegistrationFormData>({
    resolver: zodResolver(registrationFormSchema) as any, // zodResolver型の互換性のため
    defaultValues: DEFAULT_VALUES,
    mode: 'onBlur',
  });

  const {
    handleSubmit,
    formState: { errors },
    trigger,
    control,
  } = form;

  // 次のステップに進むハンドラ
  const handleNextStep = async (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault(); // デフォルト動作を確実に防止
    let fieldsToValidate: any;

    // 現在のステップに応じてバリデーション対象を決定
    switch (currentStep) {
      case 1:
        fieldsToValidate = 'basicInfo';
        break;
      case 2:
        fieldsToValidate = 'lpCustomize';
        break;
      case 3:
        fieldsToValidate = 'profile';
        break;
      case 4:
        fieldsToValidate = 'branding';
        break;
      default:
        return;
    }

    // バリデーション実行
    const isValid = await trigger(fieldsToValidate);

    if (isValid) {
      // Step 1から進む際に、名前をプロフィール名にコピー
      if (currentStep === 1) {
        const agencyName = form.getValues('basicInfo.agencyName');
        const currentProfileName = form.getValues('profile.name');
        // プロフィール名が未入力またはデフォルト値の場合のみコピー
        if (!currentProfileName || currentProfileName === '山田 太郎') {
          form.setValue('profile.name', agencyName);
        }
      }

      // デモモードでStep 4の場合は登録処理を実行
      if (isDemo && currentStep === 4) {
        const data = form.getValues();
        await handleDemoRegistration(data);
        return;
      }

      // バリデーション成功 → 次のステップへ
      setCurrentStep(Math.min(TOTAL_STEPS, currentStep + 1));
    } else {
      // バリデーション失敗 → エラーメッセージが表示される
      console.log('バリデーションエラー:', errors);
    }
  };

  // デモ登録処理ハンドラ（決済なし）
  const handleDemoRegistration = async (data: RegistrationFormData) => {
    setErrorMessage('');
    setProcessingPayment(true);
    console.log('デモ登録データ:', data);

    try {
      // Cloud Function呼び出し
      const registerAgency = httpsCallable<typeof data, RegistrationResponse>(functions, 'registerAgency');
      const result = await registerAgency(data);

      console.log('デモ登録成功:', result.data);

      if (result.data.success) {
        setRegistrationResult(result.data);
        setIsCompleted(true);
      } else {
        throw new Error(result.data.error || '登録に失敗しました');
      }
    } catch (error: any) {
      console.error('デモ登録エラー:', error);
      const message = error.message || error.toString() || '登録に失敗しました';
      setErrorMessage(message);
      alert(`登録に失敗しました\n\n${message}`);
    } finally {
      setProcessingPayment(false);
    }
  };

  // Stripe決済処理ハンドラ
  const handlePayment = async () => {
    try {
      setProcessingPayment(true);
      setErrorMessage('');

      // 匿名認証でユーザーを作成（Stripe Firebase Extensionに必要）
      let user = auth.currentUser;
      if (!user) {
        const credential = await signInAnonymously(auth);
        user = credential.user;
      }

      // 登録データを取得
      const formData = form.getValues();

      // 一意のセッションIDを生成
      const pendingRegistrationId = `pending_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // 登録データをFirestoreに一時保存（メタデータ500文字制限回避）
      await addDoc(collection(db, 'pendingRegistrations'), {
        pendingId: pendingRegistrationId,
        registrationData: {
          basicInfo: formData.basicInfo,
          lpCustomize: formData.lpCustomize,
          profile: formData.profile,
          branding: formData.branding,
        },
        createdAt: new Date(),
        userId: user.uid,
        status: 'pending',
      });

      // Firestoreにcheckout_sessionを作成
      // client_reference_idはStripeセッションに直接渡される（metadataはサブスクリプション用）
      const checkoutSessionRef = await addDoc(collection(db, 'customers', user.uid, 'checkout_sessions'), {
        price: import.meta.env.VITE_STRIPE_PRICE_ID,
        success_url: `${window.location.origin}/register/success?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}/register`,
        mode: 'subscription',
        // client_reference_idはセッションオブジェクトに渡される
        client_reference_id: `${pendingRegistrationId}|${formData.basicInfo.agencyId}`,
        // metadataはサブスクリプションオブジェクトに渡される（バックアップ用）
        metadata: {
          pendingRegistrationId: pendingRegistrationId,
          agencyId: formData.basicInfo.agencyId,
          email: formData.basicInfo.email,
        },
      });

      // StripeのCheckout URLを取得してリダイレクト
      const unsubscribe = onSnapshot(checkoutSessionRef, async (snap) => {
        const data = snap.data();

        if (data?.url) {
          // Stripe Checkoutページにリダイレクト
          window.location.href = data.url;
          unsubscribe();
        }

        if (data?.error) {
          setErrorMessage(`決済エラー: ${data.error.message}`);
          setProcessingPayment(false);
          unsubscribe();
        }
      });
    } catch (err) {
      console.error('Payment error:', err);
      setErrorMessage('決済処理の開始に失敗しました。もう一度お試しください。');
      setProcessingPayment(false);
    }
  };

  // フォーム送信ハンドラ
  const onSubmit = async (data: RegistrationFormData) => {
    // Step 4以外では登録処理を実行しない
    if (currentStep !== 4) {
      return;
    }

    setErrorMessage('');
    console.log('登録データ:', data);
    console.log('Profile photoUrl:', data.profile?.photoUrl);

    try {
      // Cloud Function呼び出し
      const registerAgency = httpsCallable<typeof data, RegistrationResponse>(functions, 'registerAgency');
      const result = await registerAgency(data);

      console.log('登録成功:', result.data);

      if (result.data.success) {
        setRegistrationResult(result.data);
        setIsCompleted(true);
      } else {
        throw new Error(result.data.error || '登録に失敗しました');
      }
    } catch (error: any) {
      console.error('登録エラー:', error);
      const message = error.message || error.toString() || '登録に失敗しました';
      setErrorMessage(message);
      alert(`登録に失敗しました

${message}`);
    }
  };

  // 完了画面
  if (isCompleted && registrationResult) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-3xl mx-auto">
          {/* 完了メッセージ */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-6">
            <div className="text-center mb-6">
              <div className="text-6xl mb-4">✅</div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">登録が完了しました！</h1>
              <p className="text-gray-600">代理店専用のアクセスURLが発行されました。</p>
            </div>

            {/* LP URL */}
            <div className="mb-6 p-6 bg-yellow-50 border border-yellow-200 rounded-md">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">いきいきスマイル共済 LP</h2>
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  readOnly
                  value={registrationResult.urls.lp}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md bg-white"
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(registrationResult.urls.lp);
                    alert('LPのURLをコピーしました');
                  }}
                  className="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-800"
                >
                  📋 コピー
                </button>
                <button
                  onClick={() => window.open(registrationResult.urls.lp, '_blank')}
                  className="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600"
                >
                  🔗 開く
                </button>
              </div>
            </div>

            {/* シミュレーター URL */}
            <div className="mb-6 p-6 bg-blue-50 border border-blue-200 rounded-md">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">共済シミュレーター</h2>
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  readOnly
                  value={registrationResult.urls.simulator}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md bg-white"
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(registrationResult.urls.simulator);
                    alert('シミュレーターのURLをコピーしました');
                  }}
                  className="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-800"
                >
                  📋 コピー
                </button>
                <button
                  onClick={() => window.open(registrationResult.urls.simulator, '_blank')}
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  🔗 開く
                </button>
              </div>
            </div>

            {/* 編集用URL */}
            <div className="mb-6 p-6 bg-green-50 border border-green-200 rounded-md">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">📝 登録情報の編集</h2>
              <p className="text-sm text-gray-600 mb-3">
                以下のURLから、いつでも登録情報を編集できます。 このURLをブックマークしておくことをお勧めします。
              </p>
              <div className="flex items-center gap-3 mb-3">
                <input
                  type="text"
                  readOnly
                  value={`https://admin.ikiiki-smile-agency.jp/edit?id=${registrationResult.shortTokenId}`}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md bg-white text-sm"
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(
                      `https://admin.ikiiki-smile-agency.jp/edit?id=${registrationResult.shortTokenId}`
                    );
                    alert('編集URLをコピーしました');
                  }}
                  className="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-800"
                >
                  📋 コピー
                </button>
              </div>
              <a
                href={`/edit?id=${registrationResult.shortTokenId}`}
                className="inline-block px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                登録情報を編集する
              </a>
            </div>

            {/* 注意事項 */}
            <div className="p-6 bg-gray-50 border border-gray-200 rounded-md">
              <h3 className="text-md font-semibold text-gray-900 mb-3">重要なお知らせ</h3>
              <ul className="text-sm text-gray-700 space-y-2 list-disc list-inside">
                <li>このURLは代理店専用のアクセスURLです</li>
                <li>URLを大切に保管してください</li>
                <li>第三者に共有しないでください</li>
                <li>登録いただいたメールアドレスにもURLを送信しました（実装予定）</li>
              </ul>
            </div>

            {/* LPを確認ボタン */}
            <div className="mt-8 text-center">
              <button
                onClick={() => window.open(registrationResult.urls.lp, '_blank')}
                className="px-8 py-3 bg-yellow-500 text-white text-lg font-semibold rounded-md hover:bg-yellow-600"
              >
                LPを確認する
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // フォーム画面
  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* ヘッダー */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {isDemo ? '【デモ登録】' : ''}いきいきスマイル共済 マイページ・シミュレーター登録
          </h1>
          <p className="text-gray-600">
            必要な情報を入力してください。すべての項目を入力すると、専用のアクセスURLが発行されます。
          </p>
          {isDemo && (
            <p className="text-sm text-orange-600 mt-2 bg-orange-50 p-2 rounded">
              ※ デモ登録のため、決済は不要です。
            </p>
          )}
        </div>

        {/* 進捗インジケーター */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex justify-between items-center">
            {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map((step) => (
              <div key={step} className="flex-1 flex items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                    currentStep >= step ? 'bg-yellow-500 text-white' : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {step}
                </div>
                {step < TOTAL_STEPS && (
                  <div className={`flex-1 h-1 mx-2 ${currentStep > step ? 'bg-yellow-500' : 'bg-gray-200'}`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2 text-xs text-gray-600">
            <span>基本情報</span>
            <span>LP設定</span>
            <span>プロフィール</span>
            <span className="text-center">
              シミュレーター
              <br />
              設定
            </span>
            {!isDemo && <span>お支払い</span>}
          </div>
        </div>

        {/* フォーム */}
        <Form {...form}>
          <form onSubmit={handleSubmit(onSubmit as any)}>
            <div className="bg-white rounded-lg shadow-md p-6">
              {/* Step 1: 基本情報 */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  <h2 className="text-xl font-bold text-gray-900 border-b pb-3">Step 1: 基本情報</h2>

                  {/* 代理店ID */}
                  <FormField
                    control={control}
                    name="basicInfo.agencyId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          代理店ID（マインクラブID） <span className="text-red-500">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input placeholder="例: 1234567890" {...field} />
                        </FormControl>
                        <FormDescription>半角数字10桁で入力してください</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* 名前 */}
                  <FormField
                    control={control}
                    name="basicInfo.agencyName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          名前 <span className="text-red-500">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input placeholder="例: 山田 太郎" {...field} />
                        </FormControl>
                        <FormDescription>お名前を入力してください</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* メールアドレス */}
                  <FormField
                    control={control}
                    name="basicInfo.email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          メールアドレス <span className="text-red-500">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input type="email" placeholder="example@example.com" {...field} />
                        </FormControl>
                        <FormDescription>登録完了通知をこのメールアドレスに送信します</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* 電話番号 */}
                  <FormField
                    control={control}
                    name="basicInfo.phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>電話番号（任意）</FormLabel>
                        <FormControl>
                          <Input type="tel" placeholder="000-0000-0000" {...field} />
                        </FormControl>
                        <FormDescription>ハイフン付きで入力してください</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}

              {/* 他のステップは今後追加 */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <h2 className="text-xl font-bold text-gray-900 border-b pb-3">Step 2: LPカスタマイズ</h2>

                  {/* ヒーロー動画選択 */}
                  <FormField
                    control={control}
                    name="lpCustomize.heroVideoKey"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          ヒーロー動画 <span className="text-red-500">*</span>
                        </FormLabel>
                        <FormControl>
                          <VideoSelector selectedVideo={field.value} onSelectVideo={field.onChange} />
                        </FormControl>
                        <FormDescription>ランディングページに表示する動画を選択してください</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* ヒーロータイトル */}
                  <FormField
                    control={control}
                    name="lpCustomize.heroTitle"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          ヒーロータイトル <span className="text-red-500">*</span>
                        </FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            rows={3}
                            placeholder="例: 人生は
選択で
変えられる"
                            className="resize-none placeholder:text-gray-300"
                          />
                        </FormControl>
                        <FormDescription>20文字以内で入力してください</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* ビジョン画像選択 */}
                  <FormField
                    control={control}
                    name="lpCustomize.visionImageKey"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          ビジョン画像 <span className="text-red-500">*</span>
                        </FormLabel>
                        <FormControl>
                          <ImageSelector selectedImage={field.value} onSelectImage={field.onChange} />
                        </FormControl>
                        <FormDescription>ビジョンセクションに表示する画像を選択してください</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}

              {currentStep === 3 && (
                <div className="space-y-6">
                  <h2 className="text-xl font-bold text-gray-900 border-b pb-3">Step 3: プロフィール</h2>

                  {/* 名前 */}
                  <FormField
                    control={control}
                    name="profile.name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          名前 <span className="text-red-500">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input placeholder="例: 山田 太郎" {...field} className="placeholder:text-gray-300" />
                        </FormControl>
                        <FormDescription>50文字以内で入力してください</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* プロフィール画像 */}
                  <FormField
                    control={control}
                    name="profile.photoUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>プロフィール画像</FormLabel>
                        <FormControl>
                          <ImageUploader currentImageUrl={field.value} onImageUploaded={field.onChange} />
                        </FormControl>
                        <FormDescription>
                          プロフィールセクションに表示される画像です。JPG、PNG、WebP形式（最大5MB）
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* 紹介文 */}
                  <FormField
                    control={control}
                    name="profile.bio"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          紹介文 <span className="text-red-500">*</span>
                        </FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            rows={8}
                            placeholder="プロフィールに表示する紹介文を入力してください"
                            className="placeholder:text-gray-300"
                          />
                        </FormControl>
                        <FormDescription>1000文字以内で入力してください</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* SNSリンク */}
                  <div className="border-t pt-4">
                    <h3 className="text-md font-semibold text-gray-900 mb-4">SNSリンク（任意）</h3>

                    {/* Instagram */}
                    <FormField
                      control={control}
                      name="profile.socialLinks.instagram"
                      render={({ field }) => (
                        <FormItem className="mb-4">
                          <FormLabel>Instagram URL</FormLabel>
                          <FormControl>
                            <Input
                              type="url"
                              placeholder="https://instagram.com/..."
                              {...field}
                              className="placeholder:text-gray-300"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* X (Twitter) */}
                    <FormField
                      control={control}
                      name="profile.socialLinks.twitter"
                      render={({ field }) => (
                        <FormItem className="mb-4">
                          <FormLabel>X (Twitter) URL</FormLabel>
                          <FormControl>
                            <Input
                              type="url"
                              placeholder="https://twitter.com/..."
                              {...field}
                              className="placeholder:text-gray-300"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* YouTube */}
                    <FormField
                      control={control}
                      name="profile.socialLinks.youtube"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>YouTube URL</FormLabel>
                          <FormControl>
                            <Input
                              type="url"
                              placeholder="https://youtube.com/..."
                              {...field}
                              className="placeholder:text-gray-300"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              )}

              {currentStep === 4 && (
                <div className="space-y-6">
                  <h2 className="text-xl font-bold text-gray-900 border-b pb-3">Step 4: シミュレーターページ設定</h2>

                  <p className="text-sm text-gray-600 bg-blue-50 p-4 rounded-md">
                    <span className="font-semibold">いきいきスマイル共済シミュレーター</span>
                    ページで表示される設定を行います。LINE公式アカウントや申し込みフォームのリンクを設定できます。すべて任意項目です。
                  </p>

                  {/* LINEリンクURL */}
                  <FormField
                    control={control}
                    name="branding.lineUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>LINEリンクURL（任意）</FormLabel>
                        <FormControl>
                          <Input type="url" placeholder="https://line.me/..." {...field} />
                        </FormControl>
                        <FormDescription>ご自身のLINEアカウントのURLを入力してください</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* 申し込みフォームURL */}
                  <FormField
                    control={control}
                    name="branding.applicationUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>あなたのマインクラブへのリンク（任意）</FormLabel>
                        <FormControl>
                          <Input type="url" placeholder="https://..." {...field} />
                        </FormControl>
                        <FormDescription>申し込みフォームURLがある場合は入力してください</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}

              {/* Step 5: お支払い情報 */}
              {currentStep === 5 && (
                <div className="space-y-6">
                  <h2 className="text-xl font-bold text-gray-900 border-b pb-3">Step 5: お支払い情報</h2>

                  <div className="text-center">
                    <p className="text-gray-600 mb-6">代理店サービスのご利用には月額料金が必要です</p>
                  </div>

                  {/* 料金プラン表示 */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <div className="text-center">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">代理店サブスクリプション</h3>
                      <div className="text-4xl font-bold text-blue-600 mb-2">
                        ¥1,500
                        <span className="text-lg font-normal text-gray-600">/月</span>
                      </div>
                      <p className="text-sm text-gray-600">税込価格</p>
                    </div>

                    <div className="mt-6 space-y-3">
                      <h4 className="font-semibold text-gray-900">含まれる機能：</h4>
                      <ul className="space-y-2 text-sm text-gray-700">
                        <li className="flex items-center">
                          <span className="mr-2">✓</span>
                          専用ランディングページ
                        </li>
                        <li className="flex items-center">
                          <span className="mr-2">✓</span>
                          共済シミュレーター利用
                        </li>
                        <li className="flex items-center">
                          <span className="mr-2">✓</span>
                          プロフィール・ブランディング設定
                        </li>
                      </ul>
                    </div>
                  </div>

                  {/* 注意事項 */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">ご注意事項</h4>
                    <ul className="space-y-1 text-sm text-gray-600">
                      <li>• 初月から課金が開始されます</li>
                      <li>• お支払いはクレジットカードのみです</li>
                      <li>• いつでも解約可能です</li>
                      <li>• 解約月も日割り計算はございません</li>
                    </ul>
                  </div>

                  {_errorMessage && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <p className="text-red-700">{_errorMessage}</p>
                    </div>
                  )}
                </div>
              )}

              {/* ナビゲーションボタン */}
              <div className="flex justify-between mt-8 pt-6 border-t">
                <button
                  type="button"
                  onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
                  disabled={currentStep === 1 || processingPayment}
                  className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  前へ
                </button>

                {currentStep < TOTAL_STEPS ? (
                  <button
                    type="button"
                    onClick={handleNextStep}
                    disabled={processingPayment}
                    className="px-6 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 disabled:opacity-50 flex items-center"
                  >
                    {processingPayment ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        処理中...
                      </>
                    ) : (
                      '次へ'
                    )}
                  </button>
                ) : isDemo ? (
                  <button
                    type="button"
                    onClick={handleNextStep}
                    disabled={processingPayment}
                    className="px-8 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 flex items-center"
                  >
                    {processingPayment ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        登録中...
                      </>
                    ) : (
                      'デモ登録する'
                    )}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handlePayment}
                    disabled={processingPayment}
                    className="px-8 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center"
                  >
                    {processingPayment ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        決済ページへ移動中...
                      </>
                    ) : (
                      'お支払いへ進む'
                    )}
                  </button>
                )}
              </div>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};
